import { Popover, Typography, IconButton } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React, { useState } from 'react'
import { CustomTrace } from '../../shared/utils/chartModel'
import { useDebouncedCallback } from 'use-debounce'
import { HexColorPicker } from 'react-colorful'
import { Slider } from '@e3dc-react/shell/Elements/Slider'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { Dash } from '../../../system-charts/shared/utils/customTrace'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { J2Multiply } from '@e3dc-react/icons'
import { usePreventReloadOnWebview } from 'src/Libs/navigation/hooks/use-prevent-reload-on-webview'
import { sendMessageToReactNative } from '@e3dc-react/shell/Libs/react-native'

const useStyles = makeStyles()(theme => ({
  /* Custom layout */
  colorPicker: {
    '& .react-colorful': {
      width: '300px',
      height: '300px',
      padding: theme.spacing(2),
    },
  },
  title: {
    textAlign: 'center',
  },
  closeButton: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

export interface TraceEditorProps {
  anchorEl: Element | null
  handlePopoverClose: () => void
  currentTraces: CustomTrace[]
  setCurrentTraces: React.Dispatch<React.SetStateAction<CustomTrace[]>>
  selectedTrace?: string
  onlyColor?: boolean
  withName?: boolean
}

const TraceEditor: React.FC<TraceEditorProps> = ({
  anchorEl,
  handlePopoverClose,
  currentTraces,
  setCurrentTraces,
  selectedTrace,
  onlyColor = false,
  withName = false,
}) => {
  const { classes } = useStyles()
  const colorPickerOpen = Boolean(anchorEl)
  const { t } = useCustomTranslation()
  const [name, setName] = useState('')
  sendMessageToReactNative({ open: colorPickerOpen, type: 'overlay' })
  usePreventReloadOnWebview(colorPickerOpen)

  const debouncedColor = useDebouncedCallback((color: string, key?: string) => {
    if (key)
      setCurrentTraces(traces =>
        traces?.map(trace => {
          if ((trace.alternateKey ?? trace.key) === key) {
            return { ...trace, color }
          }
          return trace
        })
      )
  }, 200)

  const error = currentTraces.find(trace => trace.name === name && !((trace.alternateKey ?? trace.key) === selectedTrace))

  return (
    <Popover
      open={colorPickerOpen}
      anchorEl={anchorEl}
      onClose={() => {
        if (error) {
          handlePopoverClose()
          return
        }
        if (withName) {
          setCurrentTraces(traces =>
            traces?.map(trace => {
              if ((trace.alternateKey ?? trace.key) === selectedTrace) {
                return { ...trace, alternateKey: name }
              }
              return trace
            })
          )
        }

        handlePopoverClose()
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <div className={classes.closeButton}>
        <IconButton onClick={() => handlePopoverClose()} size="large">
          <J2Multiply />
        </IconButton>
      </div>
      <div className={classes.colorPicker}>
        {withName && (
          <>
            <Typography className={classes.title}>{t('common.name')}</Typography>
            <TextField
              autoFocus
              value={currentTraces?.find(trace => (trace.alternateKey ?? trace.key) === selectedTrace)?.name}
              onChange={value => {
                setCurrentTraces(traces =>
                  traces?.map(trace => {
                    if ((trace.alternateKey ?? trace.key) === selectedTrace) {
                      return { ...trace, name: value }
                    }
                    return trace
                  })
                )
                setName(value)
              }}
              helperText={error ? t('systemDataHandler.editor.existingNameError') : ''}
            />
          </>
        )}
        {!onlyColor && (
          <>
            <Typography className={classes.title}>{t('systemDataHandler.editor.dash')}</Typography>
            <Select
              options={[
                { value: undefined, label: t('systemDataHandler.editor.line') },
                { value: 'dash', label: t('systemDataHandler.editor.dashed') },
                { value: 'dot', label: t('systemDataHandler.editor.dotted') },
              ]}
              value={currentTraces?.find(trace => (trace.alternateKey ?? trace.key) === selectedTrace)?.dash}
              onChange={dashed => {
                setCurrentTraces(traces =>
                  traces?.map(trace => {
                    if ((trace.alternateKey ?? trace.key) === selectedTrace) {
                      return { ...trace, dash: dashed as Dash | undefined }
                    }
                    return trace
                  })
                )
              }}
            />
          </>
        )}
        <Typography className={classes.title}>{t('systemDataHandler.editor.color')}</Typography>
        <HexColorPicker
          color={currentTraces?.find(trace => (trace.alternateKey ?? trace.key) === selectedTrace)?.color}
          onChange={(color: string) => {
            debouncedColor.callback(color, selectedTrace)
          }}
        />
        {!onlyColor && (
          <>
            <Typography className={classes.title}>{t('systemDataHandler.editor.thickness')}</Typography>
            <Slider
              onChange={thickness => {
                setCurrentTraces(traces =>
                  traces?.map(trace => {
                    if ((trace.alternateKey ?? trace.key) === selectedTrace) {
                      return { ...trace, thickness: thickness as number }
                    }
                    return trace
                  })
                )
              }}
              step={0.5}
              min={0.5}
              max={5}
              valueLabelDisplay={'on'}
              value={currentTraces?.find(trace => (trace.alternateKey ?? trace.key) === selectedTrace)?.thickness}
            />
          </>
        )}
      </div>
    </Popover>
  )
}

export default TraceEditor
